import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CryptoJS from 'crypto-js';
import Paper from '@material-ui/core/Paper';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import SubHeader from 'pages/account-settings/SubHeader';
import { Container } from 'styles/pages/account-setting';
import { getAllCards, removeCard, saveCard } from 'actions/stripe';
import UpdateProfile from 'pages/account-settings/UpdateProfile';
import AutomationForm from 'pages/account-settings/AutomationForm';
import PaymentOptions from 'pages/account-settings/PaymentOptions';
import {
  getUserInformation,
  automationToggle,
  deleteUnSyncedOrders,
} from 'actions/user';
import { getUserAllSyncedProducts } from 'actions/products';
import { getCountries } from 'actions/countries';

const AccountSettings = () => {
  const dispatch = useDispatch();
  const emailId = useSelector((state) => state?.auth?.profile?.emailid);
  const countries = useSelector((state) => state?.countries?.data);
  const [user, setUser] = useState(undefined);
  const [value, setValue] = useState(0);
  const [cards, setCards] = useState(undefined);
  const [syncedProducts, setSyncedProducts] = useState(undefined);

  const handleTabChange = async (event, newValue) => {
    if (newValue === 1 || newValue === 2) {
      await fetchCards();
      await fetchUserSyncedProducts();
    }

    setValue(newValue);
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!user) {
        const request = await dispatch(getUserInformation(emailId));
        if (request?.error) {
          return;
        }
        setUser(request);
      }
    };
    fetchUserDetails();
  }, [dispatch, emailId, user]);

  useEffect(() => {
    const loadCountries = async () => {
      if (!countries?.length) {
        dispatch(getCountries());
      }
    };
    loadCountries();
  }, [dispatch, countries]);

  const fetchCards = async () => {
    const response = await dispatch(
      getAllCards({
        user: emailId,
      })
    );

    if (response?.error) {
      return;
    }

    setCards(() => response);

    if (response?.length === 0) {
      const data = {
        user: user?.User?.Email,
        automation: false,
      };
      await dispatch(automationToggle(data));
      setUser(undefined);
    }
  };

  const fetchUserSyncedProducts = async () => {
    const response = await dispatch(
      getUserAllSyncedProducts({
        emailid: emailId,
      })
    );

    if (response?.error) {
      return;
    }

    setSyncedProducts(() => response);
  };

  return (
    <Container>
      {user && <SubHeader user={user} />}

      <Paper square>
        <Tabs
          value={value}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleTabChange}
        >
          <Tab label="Profile" />
          <Tab label="Automation" />
          <Tab label="Payment Options" />
        </Tabs>
      </Paper>

      {value === 0 && <UpdateProfile user={user} setUser={setUser} />}

      {value === 1 && (
        <AutomationForm
          cards={cards}
          syncedProducts={syncedProducts}
          automation={user?.automation}
          deleteUnSyncedOrders={user?.deleteUnSyncedOrders}
          setAutomation={(automation) =>
            setUser((prevState) => ({ ...prevState, automation }))
          }
          handleChange={async (automation) => {
            const data = {
              user: user?.User?.Email,
              automation,
            };
            await dispatch(automationToggle(data));
            setUser(undefined);
          }}
          handleDeleteUnSyncedOrdersChange={async (v) => {
            const data = {
              user: user?.User?.Email,
              deleteUnSyncedOrders: v,
            };
            await dispatch(deleteUnSyncedOrders(data));
            setUser(undefined);
          }}
        />
      )}

      {value === 2 && (
        <PaymentOptions
          cards={cards}
          removeCard={async (card) => {
            const request = await dispatch(
              removeCard({
                user: emailId,
                cardNumber: card?.cardNumber,
              })
            );

            if (request?.error) {
              return;
            }

            await fetchCards();
          }}
          saveCard={async (card) => {
            const cardDetails = {
              cardNumber: CryptoJS.AES.encrypt(
                card.cardNumber,
                'bot'
              ).toString(),
              cardExpMonth: CryptoJS.AES.encrypt(
                card.cardExpMonth,
                'bot'
              ).toString(),
              cardExpYear: CryptoJS.AES.encrypt(
                card.cardExpYear,
                'bot'
              ).toString(),
              cvv: CryptoJS.AES.encrypt(card.cvv, 'bot').toString(),
            };

            await dispatch(saveCard({ cardDetails, user: emailId }));
            await fetchCards();
          }}
        />
      )}
    </Container>
  );
};

export default AccountSettings;
